import { useApiGet } from '../../../../services/tanstackApiService';

// ---------------------------------------------------------------------
// Package API Types
// ---------------------------------------------------------------------

export interface PackageLevel {
  id: number;
  level_code: string;
  level_name: string;
  level: number | null;
}

export interface PackageClassRate {
  id: number;
  class_name: string;
  code: string;
  rate: string;
}

export interface PackageRule {
  id: number;
  rule_code: string;
  rule_name: string;
  rule_description: string;
  active: boolean;
  min_percent: string | null;
  max_percent: string | null;
  percentage: string | null;
  amount: string | null;
  min_amount: string | null;
  max_amount: string | null;
}

export interface Package {
  id: number;
  code: string;
  code_type: string;
  code_details: string;
  sac_code: string;
  package_name: string;
  short_description: string;
  long_description: string;
  package_amount: string;
  effective_date: string;
  expiry_date: string;
  status: boolean;
  freeze: boolean;
  levels: PackageLevel[];
  class_rates: PackageClassRate[];
  rules: PackageRule[];
}

export interface PackagesApiResponse {
  meta: {
    limit: number;
    next: string | null;
    offset: number;
    previous: string | null;
    total_count: number;
  };
  objects: Package[];
}

export interface PackageOption {
  value: string;
  label: string;
  package: Package;
  
}

// ---------------------------------------------------------------------
// Package API Hooks
// ---------------------------------------------------------------------

export interface UsePackagesOptions {
  /** Enable/disable the query */
  enabled?: boolean;
  /** Filter by status (default: true for active packages) */
  status?: boolean;
  /** Search term for filtering packages */
  search?: string;
}

/**
 * Fetches available packages from the API.
 * 
 * GET /api/packages/
 * 
 * @param options - Optional query parameters
 * @returns TanStack Query result with packages data
 * 
 * @example
 * ```tsx
 * const { data, isLoading } = usePackages({ enabled: true });
 * const packages = data?.objects || [];
 * ```
 */
export function usePackages(options?: UsePackagesOptions) {
  const { enabled = true, status = true, search } = options || {};
  
  return useApiGet<PackagesApiResponse>(
    ['packages', { status, search }],
    {
      path: 'packages/',
      query: {
        status,
        ...(search && { search }),
      },
    },
    {
      enabled,
      staleTime: 5 * 60 * 1000, // Cache for 5 minutes
    }
  );
}


export interface PharmacyItemConsumption {
  package_pharmacy_item_id: number;
  total_quantity: number;
  quantity_consumed: number;
  quantity_remaining: number;
  item_id: number;
  package_id: number;
  uom_description: string;
  discount_percent: string;
}

export interface PackageDetails {
  id: number;
  package_name: string;
  start_date: string; // ISO date string
  package_amount: number;
  package_net_amount: number;
  package_concession_amount: number;
  total_service_concession_amount: number;
  package_advance_collected: number;
  advance_collected: number;
  package_used_amount: number;
  package_balance_amount: number;
  service_amount: number;
  service_remaining_amount: number;
  pharmacy_amount: number;
  pharmacy_remaining_amount: number;
  pharmacy_component_amount: number;
  pharmacy_component_percent: number;
  collected_pharmacy_advance_amount: number;
  remaining_pharmacy_advance_amount: number;
  pharmacy_items_consumption?: PharmacyItemConsumption[];
  // Donor-linked package fields (present when fetched via donor_id)
  patient_id?: number;
  patient_name?: string;
}



export function useActivePackages(patientId?: number | string, options?: UsePackagesOptions & { donorId?: number | string }) {
  const { enabled = true, search, donorId } = options || {};
  const idParam = donorId
    ? { donor_id: donorId }
    : { patient_id: patientId };
  const resolvedId = donorId || patientId;
  
  return useApiGet<PackageDetails[]>(
    ['active-packages', { ...idParam, ...(!donorId && { pharmacy: true }), search }],
    {
      path: 'invoices/active-packages/',
      query: {
        ...idParam,
        ...(!donorId && { pharmacy: true }),
        ...(search && { search }),
      },
    },
    {
      enabled: enabled && !!resolvedId,
      staleTime: 1 * 60 * 1000, // Cache for 1 minute
      gcTime: 2 * 60 * 1000, // Keep in cache for 2 minutes
      refetchOnWindowFocus: false,
      refetchOnMount: 'always' as const,
      refetchOnReconnect: false,
      // Don't retry on 404 - it means no packages exist for this patient
      retry: (failureCount, error) => {
        // Don't retry on 404 (not found) or 400 (bad request)
        const status = (error as { status?: number })?.status;
        if (status === 404 || status === 400) {
          return false;
        }
        // Default: retry up to 3 times for other errors
        return failureCount < 3;
      },
    }
  );
}


// ---------------------------------------------------------------------
// Package Helper Functions
// ---------------------------------------------------------------------

/**
 * Maps packages API response to dropdown options.
 * Filters only active, non-frozen packages.
 * 
 * @param packages - Array of packages from API
 * @returns Array of PackageOption for dropdown
 */
export function mapPackagesToOptions(packages: Package[]): PackageOption[] {
  return packages
    .filter((pkg) => pkg.status && !pkg.freeze) // Only active, non-frozen packages
    .map((pkg) => ({
      value: String(pkg.id),
      label: `${pkg.package_name} (${pkg.code})`,
      package: pkg,
    }));
}

/**
 * Gets a package rule by rule code.
 * 
 * @param pkg - The package object
 * @param ruleCode - The rule code to find (CONCESSION, DOCTOR_SHARE, RATE_EDIT)
 * @returns The matching rule or undefined
 * 
 * @example
 * ```tsx
 * const concessionRule = getPackageRule(selectedPackage, 'CONCESSION');
 * if (concessionRule) {
 *   console.log(`Concession: ${concessionRule.min_percent}% - ${concessionRule.max_percent}%`);
 * }
 * ```
 */
export function getPackageRule(
  pkg: Package | undefined,
  ruleCode: 'CONCESSION' | 'DOCTOR_SHARE' | 'RATE_EDIT'
): PackageRule | undefined {
  return pkg?.rules?.find((rule) => rule.rule_code === ruleCode && rule.active);
}

/**
 * Gets class rate for a specific class code.
 * 
 * @param pkg - The package object
 * @param classCode - The class code to find (e.g., 'OPD', 'General Ward')
 * @returns The matching class rate or undefined
 * 
 * @example
 * ```tsx
 * const opdRate = getPackageClassRate(selectedPackage, 'OPD');
 * if (opdRate) {
 *   console.log(`OPD Rate: ${opdRate.rate}`);
 * }
 * ```
 */
export function getPackageClassRate(
  pkg: Package | undefined,
  classCode: string
): PackageClassRate | undefined {
  return pkg?.class_rates?.find((rate) => rate.code === classCode);
}

/**
 * Gets package level by level code.
 * 
 * @param pkg - The package object
 * @param levelCode - The level code to find
 * @returns The matching level or undefined
 */
export function getPackageLevel(
  pkg: Package | undefined,
  levelCode: string
): PackageLevel | undefined {
  return pkg?.levels?.find((level) => level.level_code === levelCode);
}

/**
 * Checks if a package is currently valid based on effective and expiry dates.
 * 
 * @param pkg - The package object
 * @returns true if the package is within its validity period
 */
export function isPackageValid(pkg: Package | undefined): boolean {
  if (!pkg) return false;
  
  const today = new Date();
  const effectiveDate = new Date(pkg.effective_date);
  const expiryDate = new Date(pkg.expiry_date);
  
  return today >= effectiveDate && today <= expiryDate && pkg.status && !pkg.freeze;
}

/**
 * Gets the concession range from a package's CONCESSION rule.
 * 
 * @param pkg - The package object
 * @returns Object with min and max percent, or null if no concession rule
 */
export function getPackageConcessionRange(
  pkg: Package | undefined
): { minPercent: number; maxPercent: number } | null {
  const rule = getPackageRule(pkg, 'CONCESSION');
  if (!rule) return null;
  
  return {
    minPercent: parseFloat(rule.min_percent || '0'),
    maxPercent: parseFloat(rule.max_percent || '0'),
  };
}

/**
 * Gets the doctor share percentage from a package's DOCTOR_SHARE rule.
 * 
 * @param pkg - The package object
 * @returns The doctor share percentage, or null if no rule
 */
export function getPackageDoctorShare(pkg: Package | undefined): number | null {
  const rule = getPackageRule(pkg, 'DOCTOR_SHARE');
  if (!rule || !rule.percentage) return null;
  
  return parseFloat(rule.percentage);
}

/**
 * Gets the rate edit range from a package's RATE_EDIT rule.
 * 
 * @param pkg - The package object
 * @returns Object with min and max amounts, or null if no rate edit rule
 */
export function getPackageRateEditRange(
  pkg: Package | undefined
): { minAmount: number; maxAmount: number } | null {
  const rule = getPackageRule(pkg, 'RATE_EDIT');
  if (!rule) return null;
  
  return {
    minAmount: parseFloat(rule.min_amount || '0'),
    maxAmount: parseFloat(rule.max_amount || '0'),
  };
}
